import { z } from 'zod';
import { IsoDateStringSchema, TerraSignalRowSchema } from '../schemas/terra-signals';
import { GongProbeStatusSchema } from './signal-verification';

/**
 * Wire contract for `GET /api/flow/contacts/:contactId/signals` and
 * `GET /api/flow/accounts/:accountId/signals` (plan
 * `plans/2026-09-22-contact-actions-intelligence-implementation-plan.md`
 * D1, §2a). Both routes share this one response shape: the D1 list query's
 * rows, capped at `FLOW_ENTITY_SIGNALS_LIMIT`, plus a `truncated` flag the
 * server derives from having fetched `limit + 1` rows (see
 * `src/services/terra-signals/query.ts`'s `listTerraSignalsByScope`) — no
 * second COUNT round trip.
 */

/** The wire-facing page size for both entity-signals routes (distinct from `TERRA_SIGNALS_LIST_DEFAULT_LIMIT`, which is the query layer's own default — the two happen to agree today but are independent constants). */
export const FLOW_ENTITY_SIGNALS_LIMIT = 100;

/**
 * `TerraSignalRow` plus the Gong verification probe's `verified_at`
 * (`gong_flow_probe`, joined by `listTerraSignalsByScope` — plan
 * `plans/signals-tab-pr1/qa-batch2-plan.md` D-E / unit B3). Non-null only
 * when a probe row exists for `(contact_id, signal_instance_id)`; the
 * Timeline and the Signal card resolve verification state from it via
 * `resolveSignalVerificationState` (unit B2). A new, single-declaration
 * schema name — `TerraSignalRowSchema` keeps its own `.extend()`
 * (`terra/no-duplicate-schema-name`).
 */
export const EntitySignalRowSchema = TerraSignalRowSchema.extend({
  gong_verified_at: IsoDateStringSchema.nullable(),
  /** The probe's own normalized verdict, from the SAME probe row as `gong_verified_at` (`decorateGongVerification`'s `probe_status`, fix-plan-batch2.md D-A); `null` when no probe row exists. */
  gong_probe_status: GongProbeStatusSchema,
});
export type EntitySignalRow = z.infer<typeof EntitySignalRowSchema>;

export const FlowEntitySignalsResponseSchema = z.object({
  rows: z.array(EntitySignalRowSchema),
  truncated: z.boolean(),
});
export type FlowEntitySignalsResponse = z.infer<typeof FlowEntitySignalsResponseSchema>;
